<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { contentAttributes, content, contentType } = useMessageContext();

const cards = computed(() => {
  return contentAttributes.value?.items || [];
});

const buttons = computed(() => {
  return contentAttributes.value?.buttons || [];
});

const isFacebookButtons = computed(() => {
  return contentType.value === 'fb_buttons';
});

const isCoupon = computed(() => {
  return contentType.value === 'coupon';
});

const couponCode = computed(() => {
  return contentAttributes.value?.coupon_code || '';
});

const couponUrl = computed(() => {
  return contentAttributes.value?.coupon_url || '';
});

const openUrl = url => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const handlePostback = payload => {
  // For now, just log - this could be extended to send the postback
  console.log('Postback action:', payload);
};

const handleAction = action => {
  if (action.type === 'link' && action.uri) {
    openUrl(action.uri);
  } else if (action.type === 'postback' || action.payload) {
    handlePostback(action.payload || action.text);
  }
};

const handleButtonClick = button => {
  if (button.type === 'web_url' && button.url) {
    openUrl(button.url);
  } else if (button.type === 'postback' && button.payload) {
    handlePostback(button.payload);
  }
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <BaseBubble
      v-if="content"
      class="px-4 py-3"
      data-bubble-name="cards-header"
    >
      <span v-dompurify-html="content" :title="content" />
    </BaseBubble>

    <!-- Facebook Button Template -->
    <div
      v-if="isFacebookButtons"
      class="bg-n-slate-4 text-n-slate-12 rounded-xl overflow-hidden max-w-xs p-3"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="(button, index) in buttons"
          :key="index"
          class="px-4 py-2 bg-n-solid-blue hover:bg-n-blue-10 text-n-slate-12 rounded-lg text-sm font-medium transition-colors cursor-pointer border-none"
          @click="handleButtonClick(button)"
        >
          {{ button.title }}
        </button>
      </div>
    </div>

    <!-- Coupon Template -->
    <div
      v-if="isCoupon && couponCode"
      class="bg-n-slate-4 text-n-slate-12 rounded-xl overflow-hidden max-w-xs p-3"
    >
      <div class="flex flex-col gap-2">
        <button
          class="px-4 py-2 bg-n-solid-blue hover:bg-n-blue-10 text-n-slate-12 rounded-lg text-sm font-medium transition-colors cursor-pointer border-none font-mono tracking-wider"
        >
          {{ couponCode }}
        </button>

        <button
          v-if="couponUrl"
          class="px-4 py-2 bg-n-solid-blue hover:bg-n-blue-10 text-n-slate-12 rounded-lg text-sm font-medium transition-colors cursor-pointer border-none"
          @click="openUrl(couponUrl)"
        >
          View Offer
        </button>
      </div>
    </div>

    <!-- Cards/Generic Template -->
    <div
      v-for="(card, index) in cards"
      v-else-if="!isFacebookButtons && !isCoupon"
      :key="index"
      class="bg-n-slate-4 text-n-slate-12 rounded-xl overflow-hidden max-w-xs"
    >
      <img
        v-if="card.media_url"
        class="w-full object-cover max-h-[200px]"
        :src="card.media_url"
        :alt="card.title"
      />

      <div class="p-4">
        <h4 class="font-semibold text-base mb-1 text-n-slate-12">
          {{ card.title }}
        </h4>

        <p v-if="card.description" class="text-sm text-n-slate-11 mb-3">
          {{ card.description }}
        </p>

        <div
          v-if="card.actions && card.actions.length"
          class="flex flex-col gap-2"
        >
          <button
            v-for="(action, actionIndex) in card.actions"
            :key="actionIndex"
            class="px-4 py-2 bg-n-solid-blue hover:bg-n-blue-10 text-n-slate-12 rounded-lg text-sm font-medium transition-colors cursor-pointer border-none"
            @click="handleAction(action)"
          >
            {{ action.text }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
